import { find, findKey, get, isEmpty, isNumber, isUndefined } from 'lodash';
import moment from 'moment-timezone';
import {
  celsiusToFahrenheit,
  fahrenheitToCelsius,
  kilogramsToPounds,
  poundsToKilograms,
} from '../../utilities/helpers/commonHelper';

moment.updateLocale('en', {
  relativeTime: {
    future: '%s',
    past: '%s',
    s: 'Just Now',
    ss: 'Just Now',
    m: '1 min ago',
    mm: '%d min ago',
    h: '1 hour ago',
    hh: '%d hours ago',
    d: '1 day ago',
    dd: '%d days ago',
    w: '1 week ago',
    ww: '%d weeks ago',
    M: '1 month ago',
    MM: '%d months ago',
    y: '1 year ago',
    yy: '%d years ago',
  },
});

export const wellnessListModel = {
  medical_accessories: {
    blood_pressure: {
      day: '',
      time: '',
      timeDiff: '',
      data: {
        systolic: { display_label: '', display_value: '' },
        diastolic: { display_label: '', display_value: '' },
        pulse: { display_label: '', display_value: '' },
      },
    },
    pulse_oximeter: {
      day: '',
      time: '',
      timeDiff: '',
      data: {
        oxygen: { display_label: '', display_value: '' },
        pulse: { display_label: '', display_value: '' },
      },
    },
    weight_scale: {
      day: '',
      time: '',
      timeDiff: '',
      data: { weight: { display_label: '', display_value: '' } },
    },
    thermometer: {
      day: '',
      time: '',
      timeDiff: '',
      data: { temperature: { display_label: '', display_value: '' } },
    },
    glucose_meter: {
      day: '',
      time: '',
      timeDiff: '',
      data: { glucose_concentration: { display_label: '', display_value: '' } },
    },
  },
};

export const unitTitleData = {
  metric: {
    temperature: 'Degrees celsius °',
    weight: 'Weight (kgs)',
    systolic: 'Systolic mmHg',
    diastolic: 'Diastolic mmHg',
    pulse: 'Pulse beats/min',
    pulse_rate: 'Pulse beats/min',
    oxygen: 'SpO2 %',
    spo2: 'SpO2 %',
    glucose_concentration: 'mg/dl',
  },
  imperial: {
    temperature: 'Fahrenheit',
    weight: 'Weight (lbs)',
    systolic: 'Systolic mmHg',
    diastolic: 'Diastolic mmHg',
    pulse: 'Pulse beats/min',
    pulse_rate: 'Pulse beats/min',
    oxygen: 'SpO2 %',
    spo2: 'SpO2 %',
    glucose_concentration: 'mg/dl',
  },
  device_title: {
    blood_pressure: 'Blood Pressure',
    pulse_oximeter: 'Pulse Oximeter',
    thermometer: 'Body Temperature',
    weight_scale: 'Body Weight',
    glucose_meter: 'Glucose Meter',
  },
};

export const wellnessEventTypes = {
  BLOOD_PRESSURE: 'blood_pressure_reading',
  GLUCOSE: 'glucose_meter_reading',
  PULSE_OXIMETER: 'pulse_oximeter_reading',
  THERMOMETER: 'thermometer_reading',
  WEIGHT_SCALE: 'weight_scale_reading',
};

const wellnessEventDataKeys = {
  MESSAGE_TYPE: 'message_type',
  UTC_TIMESTAMP: 'utc_timestamp_inms',
  DEVICE_UUID: 'deviceuuid',
  DEVICE_MODEL: 'device_model',
  DEVICE_MANUFACTURER: 'device_manufacturer',
  EVENT_KEY_MAP: [
    { EVENT_TYPE: 'blood_pressure_reading', KEY: 'blood_pressure' },
    { EVENT_TYPE: 'glucose_meter_reading', KEY: 'glucose_meter' },
    { EVENT_TYPE: 'pulse_oximeter_reading', KEY: 'pulse_oximeter' },
    { EVENT_TYPE: 'thermometer_reading', KEY: 'thermometer' },
    { EVENT_TYPE: 'weight_scale_reading', KEY: 'weight_scale' },
  ],
  UNIT_TYPE_MAP: {
    imperial: ['lbs', 'F'],
    metric: ['kgs', 'C'],
  },
  BLOOD_PRESSURE: {
    systolic: { value: 'blood_pressure_reading__systolic' },
    diastolic: { value: 'blood_pressure_reading__diastolic' },
    pulse: { value: 'blood_pressure_reading__pulse_rate' },
  },
  GLUCOSE: {
    glucose_concentration: {
      value: 'glucose_meter_reading__glucose_concentration',
    },
  },
  PULSE_OXIMETER: {
    oxygen: { value: 'pulse_oximeter_reading__spo2' },
    pulse: { value: 'pulse_oximeter_reading__pulse_rate' },
  },
  THERMOMETER: {
    temperature: {
      value: 'thermometer_reading__temperature',
      unit: 'thermometer_reading__temperature_unit',
    },
  },
  WEIGHT_SCALE: {
    weight: {
      value: 'weight_scale_reading__weight',
      unit: 'weight_scale_reading__weight_unit',
    },
  },
};

export const getStatisticUnitTitle = (val, userDefinedMeasurement = 'imperial') => {
  const valKey = val.toLowerCase();
  return get(unitTitleData, `${userDefinedMeasurement}.${valKey}`, '');
};

export const formatValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') {
    const roundedValue = Math.round(value * 100) / 100; // Round to 2 decimal places
    return roundedValue.toString().length > 5
      ? roundedValue.toFixed(2).slice(0, 5) // Truncate if longer than 5 characters
      : roundedValue.toString();
  }
  return value.toString().slice(0, 5); // For non-numeric values
};

export const formatValueV2 = (value) => {
  // WellnessHistoryInsightChart formatting is different. so using the same here.
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number' && !Number.isInteger(value)) {
    const roundedValue = Math.round(value * 100) / 100; // round to 2 decimal places
    return roundedValue.toString().length > 5
      ? roundedValue.toFixed(1).slice(0, 5)
      : roundedValue.toFixed(1).toString();
  } else {
    return value;
  }
};

const formatTimestamp = (timestamp, timezone, is24hoursformat) => {
  if (isEmpty(timestamp)) return { day: '-', time: '-', timeDiff: '-' };

  const now = moment.tz(timezone);
  const inputTime = moment(timestamp).tz(timezone);
  const daysDiff = now.diff(inputTime, 'days');
  const day = daysDiff === 0 ? 'Today' : daysDiff === 1 ? 'Yesterday' : inputTime.format('ddd M/D');
  const time = inputTime.format(is24hoursformat ? 'HH:mm' : 'h:mm A');
  const timeDiff = inputTime.fromNow();

  return { day, time, timeDiff };
};

const isTimestampLatest = (timestamp, prevTimestamp, timezone) => {
  const latest = moment.tz(timestamp, timezone);
  const prev = moment.tz(prevTimestamp, timezone);
  return latest.isAfter(prev);
};

const convertToUserDefinedUnitVal = (key, value, unit, userDefinedMeasurement = 'imperial') => {
  if (isEmpty(key) || !isNumber(value)) return null;

  if (['weight', 'temperature'].includes(key)) {
    if (isEmpty(unit)) return value;

    const valueUnitType = findKey(wellnessEventDataKeys.UNIT_TYPE_MAP, (m) => m.includes(unit));

    if (isUndefined(valueUnitType) || userDefinedMeasurement === valueUnitType) return value;

    if (userDefinedMeasurement === 'metric' && valueUnitType === 'imperial') {
      if (key === 'weight') return poundsToKilograms(value);
      if (key === 'temperature') return fahrenheitToCelsius(value);
    }

    if (userDefinedMeasurement === 'imperial' && valueUnitType === 'metric') {
      if (key === 'weight') return kilogramsToPounds(value);
      if (key === 'temperature') return celsiusToFahrenheit(value);
    }
  }

  return value;
};

const processDataFromEvent = (latestEvent, eventType, key, userDefinedMeasurement) => {
  let data = [];
  Object.keys(wellnessListModel['medical_accessories'][key]['data']).forEach((k) => {
    let value;
    let unit;
    switch (eventType) {
      case wellnessEventTypes.BLOOD_PRESSURE: {
        if (Object.keys(wellnessEventDataKeys.BLOOD_PRESSURE).includes(k)) {
          value = latestEvent[wellnessEventDataKeys.BLOOD_PRESSURE[k].value];
        }
        break;
      }
      case wellnessEventTypes.GLUCOSE: {
        if (Object.keys(wellnessEventDataKeys.GLUCOSE).includes(k)) {
          value = latestEvent[wellnessEventDataKeys.GLUCOSE[k].value];
        }
        break;
      }
      case wellnessEventTypes.PULSE_OXIMETER: {
        if (Object.keys(wellnessEventDataKeys.PULSE_OXIMETER).includes(k)) {
          value = latestEvent[wellnessEventDataKeys.PULSE_OXIMETER[k].value];
        }
        break;
      }
      case wellnessEventTypes.THERMOMETER: {
        if (Object.keys(wellnessEventDataKeys.THERMOMETER).includes(k)) {
          value = latestEvent[wellnessEventDataKeys.THERMOMETER[k].value];
          unit = latestEvent[wellnessEventDataKeys.THERMOMETER[k].unit];
        }
        break;
      }
      case wellnessEventTypes.WEIGHT_SCALE: {
        if (Object.keys(wellnessEventDataKeys.WEIGHT_SCALE).includes(k)) {
          value = latestEvent[wellnessEventDataKeys.WEIGHT_SCALE[k].value];
          unit = latestEvent[wellnessEventDataKeys.WEIGHT_SCALE[k].unit];
        }
        break;
      }
      default:
        break;
    }
    const label = getStatisticUnitTitle(k, userDefinedMeasurement);
    value = formatValue(convertToUserDefinedUnitVal(k, value, unit, userDefinedMeasurement));
    data.push({
      display_label: label,
      display_value: `${value}${!isEmpty(unit) && unit === 'F' ? '°' : ''}`,
    });
  });
  return data;
};

export const processEventFromStream = ({
  latestEvent,
  userInfo,
  timezone,
  setWellnessListData,
  loginUserMeasurementUnit,
  loginUserTimeformat,
}) => {
  if (isEmpty(latestEvent)) return;

  setWellnessListData((prev) => {
    const is24hoursformat = get(loginUserTimeformat, 'is24hoursformat', false);
    const userDefinedMeasurement = get(loginUserMeasurementUnit, 'measurement', 'imperial');
    const eventType = latestEvent[wellnessEventDataKeys.MESSAGE_TYPE];
    let timestamp = latestEvent[wellnessEventDataKeys.UTC_TIMESTAMP];
    const deviceUuid = get(latestEvent, wellnessEventDataKeys.DEVICE_UUID, '');
    const deviceManufacturer = get(latestEvent, wellnessEventDataKeys.DEVICE_MANUFACTURER, '');
    const deviceModel = get(latestEvent, wellnessEventDataKeys.DEVICE_MODEL, '');
    const key = findKey(get(prev, 'medical_accessories', {}), ['eventName', eventType]);

    /**
     * Formatting the timestamp
     */
    if (isNumber(timestamp)) {
      timestamp = moment(timestamp).tz(timezone).toISOString();
    }
    const { day, time, timeDiff } = formatTimestamp(timestamp, timezone, is24hoursformat);

    /**
     * Handle the scenario when any single medical data is not available
     * in the wellnessListData object
     */
    if (isUndefined(key)) {
      const eventKeyMap = find(wellnessEventDataKeys.EVENT_KEY_MAP, ['EVENT_TYPE', eventType]);
      if (isUndefined(eventKeyMap)) return prev;

      return {
        medical_accessories: {
          ...prev.medical_accessories,
          [eventKeyMap.KEY]: {
            title: eventKeyMap.KEY,
            day,
            time,
            timeDiff,
            utc_timestamp_inms: timestamp,
            data: processDataFromEvent(
              latestEvent,
              eventType,
              eventKeyMap.KEY,
              userDefinedMeasurement
            ),
            deviceManufacture: deviceManufacturer,
            deviceModel,
            device_uuid: deviceUuid,
            eventName: eventType,
          },
        },
      };
    }

    /**
     * Handle the scenario when no medical data is not available in the
     * wellnessListData object
     */
    if (isEmpty(get(prev, 'medical_accessories', {}))) {
      return {
        medical_accessories: {
          [key]: {
            title: key,
            day,
            time,
            timeDiff,
            utc_timestamp_inms: timestamp,
            data: processDataFromEvent(latestEvent, eventType, key, userDefinedMeasurement),
            deviceManufacture: deviceManufacturer,
            deviceModel,
            device_uuid: deviceUuid,
            eventName: eventType,
          },
        },
      };
    }

    /**
     * Handle a scenario where the timestamp in the latestEvent is not
     * the latest one available
     */
    const prevTimestamp = prev['medical_accessories'][key]['utc_timestamp_inms'];
    if (!isTimestampLatest(timestamp, prevTimestamp, timezone)) return prev;

    return {
      medical_accessories: {
        ...prev.medical_accessories,
        [key]: {
          ...prev.medical_accessories[key],
          title: key,
          day,
          time,
          timeDiff,
          utc_timestamp_inms: timestamp,
          data: processDataFromEvent(latestEvent, eventType, key, userDefinedMeasurement),
          eventName: eventType,

          /**
           * Handle the scenario where the device sending the latest event
           * is different from the previous one
           */
          ...(get(prev, `medical_accessories.${key}.device_uuid`, '') !== deviceUuid
            ? {
                deviceManufacture: deviceManufacturer,
                deviceModel,
                device_uuid: deviceUuid,
              }
            : {}),
        },
      },
    };
  });
};

export const getUserDefinedUnitVal = (value, key, measurementSystem) => {
  if (!value || !key) {
    console.error('Invalid arguments provided.');
    return null;
  }
  const userDefinedMeasurement = measurementSystem || '';
  const valueUnitType = value.unit_type || '';

  if (!userDefinedMeasurement || userDefinedMeasurement === '' || valueUnitType === '') {
    return value.value;
  }

  if (String(userDefinedMeasurement) === String(valueUnitType)) {
    return value.value;
  }

  if (userDefinedMeasurement === 'metric' && valueUnitType === 'imperial') {
    if (key === 'weight_scale') return poundsToKilograms(value.value);
    if (key === 'thermometer') return fahrenheitToCelsius(value.value);
    // [HOMESIGHTVTV-9271] Removed glucose meter conversion
    // if (key === 'glucose_meter') return mgTommol(value.value);
  }

  if (userDefinedMeasurement === 'imperial' && valueUnitType === 'metric') {
    if (key === 'weight_scale') return kilogramsToPounds(value.value);
    if (key === 'thermometer') return celsiusToFahrenheit(value.value);
    // [HOMESIGHTVTV-9271] Removed glucose meter conversion
    // if (key === 'glucose_meter') return mmolTomg(value.value);
  }

  return value.value;
};
